<template>
  <tr class="table-row" :class="{'table-row--parent': row.hasBackorderedItems}">
    <!-- Each Cell -->
    <template v-for="column in grid.headers">
      <td
        v-if="showColumn(column)"
        :key="`${row[rowId]}-${row[rowKey]}-${column.key}`"
        class="b2b-grid__cell lg:table-cell"
        :class="[
          column.key,
          column.cssClasses,
          {'mb-4 md:mb-0': row.hasBackorderedItems && column.key === 'backOrders'},
        ]"
      >
        <div class="b2b-grid__label" v-html="column.label"></div>

        <div class="b2b-grid__content">
          <!-- Not Shippable cell -->
          <div v-if="column.key === 'notShippable'">
            <div v-if="row.isGreenShipping">
              <div :id="`greenShipping-tooltip-${typeId}-${rowId}`" v-tippy-html>
                <span v-html="bp('greenShipping')" />
              </div>
              <span>
                <i
                  v-tippy="{
                    html: `#greenShipping-tooltip-${typeId}-${rowId}`,
                    interactive: true,
                    reactive: true,
                    trigger: 'mouseenter focus',
                    flip: true,
                    sticky: true,
                  }"
                  class="b2b-grid__icon inline-block cursor-pointer"
                  :qaid="`green-shipping-icon-${typeId}-${rowId}`"
                >
                  <svg viewBox="0 0 24 24" width="1.125rem" height="1.125rem" fill="var(--green-100)">
                    <path
                      d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"
                    />
                  </svg>
                </i>
              </span>
            </div>
            <div v-else-if="row.isAllowedToShip === false && row.allocatedQuantity > 0">
              <div :id="`notShippable-tooltip-${typeId}-${rowId}`" v-tippy-html>
                <span v-html="bp('notShippable')" />
              </div>
              <span>
                <i
                  v-tippy="{
                    html: `#notShippable-tooltip-${typeId}-${rowId}`,
                    interactive: true,
                    reactive: true,
                    trigger: 'mouseenter focus',
                    flip: true,
                    sticky: true,
                  }"
                  class="b2b-grid__icon material-icons cursor-pointer"
                  :qaid="`not-shippable-icon-${typeId}-${rowId}`"
                  >block</i
                >
              </span>
            </div>
          </div>
          <!-- Cancel Date cell -->
          <div v-else-if="column.key === 'cancelDate'">
            <i v-if="row[column.key] && row[column.key].length > 0" class="b2b-grid__icon material-icons text-warning"
              >warning</i
            >
            <span v-html="row[column.key]"></span>
          </div>
          <!-- Stock cell -->
          <div v-if="column.key === 'quantityInWarehouses'">
            <div v-if="row[column.key] && row[column.key].warehouses && row[column.key].warehouses.length > 0">
              <div
                v-for="warehouse in row[column.key].warehouses"
                :key="warehouse.id"
                class="b2b-grid__warehouse no-wrap"
              >
                {{ warehouse.id }},
                <span :data-level="warehouse.stockLevelLabel" class="inline-block">{{
                  warehouse.stockLevel || 'zero'
                }}</span>
              </div>
            </div>
          </div>
          <!-- Backorders cell -->
          <div v-else-if="column.key === 'backOrders'">
            <!-- <i v-show="showCancelWarning" class="b2b-grid__icon material-icons text-warning">warning</i> -->
            <i
              v-show="row.hasBackorderedItems || row.hasAllocatedItems"
              class="b2b-grid__icon material-icons"
              @click.prevent="row.expanded = !row.expanded"
              >{{ row.expanded ? 'indeterminate_check_box' : 'add_box' }}</i
            >
          </div>
          <!-- Order Number cell -->
          <a v-else-if="column.key === 'orderNumber'" :href="orderLinkUrl" class="underline hover:no-underline">
            <span v-html="row[column.key]"></span>
          </a>
          <!-- ETA Watch cell -->
          <div v-else-if="column.key === 'etaWatch'">
            <form :id="`etaWatchForm-${typeId}-${rowId}`"></form>
            <div v-if="row.productType === 'Project One Bikes'">
              <div :id="`p1-eta-tooltip-${typeId}-${rowId}`" v-tippy-html>
                {{ bp('p1Watch') }}
              </div>
              <button
                v-tippy="{
                  html: `#p1-eta-tooltip-${typeId}-${rowId}`,
                  interactive: true,
                  reactive: true,
                  trigger: 'mouseenter focus',
                  flip: true,
                  sticky: true,
                }"
                class="b2b-grid__stock-watch__indicator disabled"
                :class="[{'text-success': row.etaWatch === true}]"
              >
                <i class="b2b-grid__stock-watch__icon material-icons">{{
                  row.etaWatch === true ? 'visibility' : 'visibility_off'
                }}</i>
                <span class="b2b-grid__stock-watch__label font-medium">{{ getBaseProperty('watch') }}</span>
              </button>
            </div>
            <div v-else>
              <button
                :disabled="isProcessing"
                class="b2b-grid__stock-watch__indicator"
                :class="[{'text-success': row.etaWatch === true}]"
                @click="updateEtaWatch(row)"
              >
                <i class="b2b-grid__stock-watch__icon material-icons">{{
                  row.etaWatch === true ? 'visibility' : 'visibility_off'
                }}</i>
                <span class="b2b-grid__stock-watch__label b2b-grid__stock-watch__label font-medium">{{
                  getBaseProperty('watch')
                }}</span>
              </button>
            </div>
          </div>
          <!-- line note cell -->
          <div v-else-if="column.key === 'lineNote'">
            <grid-note
              v-if="!row.isConsumer"
              :data="{
                value: 'note_add',
                editable: true,
                content: row.lineComment ? row.lineComment : '',
                hasNotification: row.lineComment != null && row.lineComment.length,
              }"
              :item-id="rowId"
              :max-length="60"
              :base-properties="baseProperties"
              @note-added="addNote"
            />
          </div>
          <!-- Description cell -->
          <div v-else-if="column.key === 'description'">
            <div v-if="row.productType === 'Project One Bikes'" class="flex">
              <div :id="`p1-tooltip-icon-${typeId}-${rowId}`" v-tippy-html>{{ bp('p1') }}</div>
              <span
                v-tippy="{
                  html: `#p1-tooltip-icon-${typeId}-${rowId}`,
                  interactive: true,
                  reactive: true,
                  trigger: 'mouseenter focus',
                  flip: true,
                  sticky: true,
                  theme: 'light left-align',
                }"
                class="colorWheel flex-none self-center cursor-pointer mr-1"
                :qaid="`p1-bike-color-wheel-${typeId}-${rowId}`"
              />
              <span v-html="row[column.key]" />
            </div>
            <div v-else class="flex">
              <div :id="`p1-tooltip-icon-${typeId}-${rowId}`" v-tippy-html>{{ bp('p1BTOHover') }}</div>
              <span
                v-tippy="{
                  html: `#p1-tooltip-icon-${typeId}-${rowId}`,
                  interactive: true,
                  reactive: true,
                  trigger: 'mouseenter focus',
                  flip: true,
                  sticky: true,
                  theme: 'light left-align',
                }"
                :class="
                  `${
                    row.productSubType === 'P1BTO'
                      ? 'colorWheel filter grayscale flex-none self-center cursor-pointer mr-1'
                      : ''
                  }`
                "
              />
              <span v-html="row[column.key]" />
            </div>
          </div>
          <!-- SKU cell -->
          <div v-else-if="column.key === 'sku' && linked">
            <a
              v-if="row.productType !== 'Project One Bikes'"
              :href="`${encodedContextPath}/p/${row[column.key]}`"
              class="underline hover:no-underline"
            >
              <span v-html="row[column.key]" />
            </a>
            <span v-else v-html="row[column.key]" />
          </div>
          <!-- All other cells -->
          <span v-else v-html="row[column.key]" />
        </div>
      </td>
    </template>
  </tr>
</template>

<script>
import axios from 'axios';
import some from 'lodash/some';
import {mapState, mapGetters} from 'vuex';
import GridNote from '@/components/grid/GridNote';

export default {
  inject: ['grid'],
  components: {
    GridNote,
  },
  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    archiveEnabled: Boolean,
    row: {
      type: [Object, Array],
      default: undefined,
    },
    rowId: {
      type: [String, Number],
      default: undefined,
    },
    rowKey: {
      type: String,
      default: 'id',
    },
    typeId: {
      type: String,
      default: 'bikes',
    },
  },
  data() {
    return {
      isProcessing: false,
    };
  },
  computed: {
    /**
     * Show a warning icon if any of the backordered items on an order has a cancel date
     * seven or fewer days away.
     */
    showCancelWarning() {
      if (!this.row.hasBackorderedItems) {
        return false;
      }

      const allItems = [...this.row.allocatedItems, ...this.row.notAllocatedItems];

      return some(allItems, {showCancelFlag: true});
    },

    orderLinkUrl() {
      const {orderNumber, orderType, orderCompany} = this.row;

      return `${this.encodedContextPath}/my-account/order/${orderNumber}?company=${orderCompany}&ordertype=${orderType}`;
    },

    linked() {
      if (this.row.baseProductArchived) {
        /**
         *  Disable the link to the pdp if base product is archived and functionality archive toggle is not enabled.
         */
        if (!this.archiveEnabled) {
          return false;
        }
      } else {
        /**
         *  Disable the link to the pdp if base product is not archived but the variant is archived.
         */
        if (this.row.variantProductArchived) {
          return false;
        }
      }
      /**
       *  Enable the link to the pdp.
       */
      return true;
    },

    ...mapState('backend', ['encodedContextPath']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },
  created() {
    /**
     * Add the 'expanded' property to the order to track the open/closed state of the
     * secondary row for backordered items
     */
    if (this.row.expanded === undefined) {
      this.$set(this.row, 'expanded', false);
    }
    if (!this.row.allocatedItems) {
      this.$set(this.row, 'allocatedItems', []);
    }
    if (!this.row.notAllocatedItems) {
      this.$set(this.row, 'notAllocatedItems', []);
    }
  },
  methods: {
    showColumn(column) {
      // Don't show the Backorders column in Invoiced orders tab
      if (column.key === 'backOrders' && this.grid.orderStatus === 'pastOrders') {
        return false;
      }
      if (this.consumerFriendlyMode && (column.key == 'price' || column.key == 'orderTotal')) return false;
      return true;
    },
    addNote(note) {
      const payload = {
        orderLineNumber: this.row.orderLineNumber,
        orderNumber: this.row.orderNumber,
        orderType: this.row.orderType,
        orderCompany: this.row.orderCompany,
        lineComment: note.content,
      };
      axios
        .post(`/allocated/item/comment/update`, payload)
        .then((response) => {
          if (response.data.meta.feedback.type === 'success') {
            this.$set(this.row, 'lineComment', note.content);
            this.$forceUpdate();
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    updateEtaWatch(item) {
      const payload = {
        sku: item.sku,
        warehouse: item.warehouse,
        orderLineNumber: item.orderLineNumber,
        orderNumber: item.orderNumber,
        orderType: item.orderType,
        orderCompany: item.orderCompany,
      };
      this.isProcessing = true;
      // P1 items cannot change watch status
      if (item.productType !== 'Project One Bikes') {
        // If the item already has an ETA Watch, we send a delete request
        if (item.etaWatchId && item.etaToggle === true) {
          axios
            .delete(`/my-account/etawatch/${item.etaWatchId}`, payload)
            .then(() => {
              item.etaToggle = false;
              item.etaWatch = false;
              item.etaWatchId = null;
              this.isProcessing = false;
            })
            .catch((error) => console.log(error));
          // If the item has no ETA Watch then send a POST request
        } else {
          axios
            .post('/my-account/etawatch', payload)
            .then(({data}) => {
              item.etaToggle = data.data.success;
              item.etaWatch = data.data.success;
              item.etaWatchId = data.data.id;
              this.isProcessing = false;
            })
            .catch((error) => console.log(error));
        }
      }
    },
  },
  provide() {
    return {
      item: this,
    };
  },
};
</script>
